/* eslint-disable */

import _ from "lodash";
import { Promise } from "es6-promise";

/**
 * --------------------------------------------------
 * Array utilities
 * --------------------------------------------------
 */
export const head = (a) => a[0];

export const forEach = (a, fn) => Array.prototype.forEach.call(a, fn);

export const filter = (a, fn) => Array.prototype.filter.call(a, fn);

export const map = (a, fn) => Array.prototype.map.call(a, fn);

export const nextTick = (fn) => setTimeout(fn, 0);

/**
 * --------------------------------------------------
 * Object utilities
 * --------------------------------------------------
 */
export const objectByKey = (arr, key) =>
  arr.reduce((prev, curr) => {
    if (typeof curr[key] === "undefined") {
      return prev;
    }
    prev[curr[key]] = curr;
    return prev;
  }, {});

export const getValues = (o) =>
  Object.keys(o).reduce((values, key) => values.concat(o[key]), []);

export const prop = _.curry((p, o) => o[p]);

/**
 * --------------------------------------------------
 * DOM utilities
 * --------------------------------------------------
 */
export const getAttributes = (attrs, el) =>
  attrs.map((x) => el.getAttribute(x));

export const getScrollPosition = () => {
  const supportPageOffset = window.pageXOffset !== undefined;
  const isCSS1Compat = (document.compatMode || "") === "CSS1Compat";

  /* eslint-disable no-nested-ternary */
  const x = supportPageOffset
    ? window.pageXOffset
    : isCSS1Compat
      ? document.documentElement.scrollLeft
      : document.body.scrollLeft;

  const y = supportPageOffset
    ? window.pageYOffset
    : isCSS1Compat
      ? document.documentElement.scrollTop
      : document.body.scrollTop;
  return {
    x,
    y,
  };
};

export const closest = (elm, selector, includingSelf = true) => {
  let matchesFn;

  // find vendor prefix
  [
    "matches",
    "webkitMatchesSelector",
    "mozMatchesSelector",
    "msMatchesSelector",
    "oMatchesSelector",
  ].some((fn) => {
    if (typeof document.body[fn] === "function") {
      matchesFn = fn;
      return true;
    }
    return false;
  });

  // check self
  if (
    includingSelf &&
    elm[matchesFn] !== undefined &&
    elm[matchesFn](selector)
  ) {
    return elm;
  }

  // traverse parents
  while (elm !== null) {
    const parent = elm.parentNode;
    if (
      parent !== null &&
      parent[matchesFn] !== undefined &&
      parent[matchesFn](selector)
    ) {
      return parent;
    }
    elm = parent;
  }

  return null;
};

// next until
export const nextUntil = (elm, selector) => {
  // Setup siblings array
  const siblings = [];

  // Get the next sibling element
  elm = elm.nextElementSibling;

  // As long as a sibling exists
  while (elm) {
    // If we've reached our match, bail
    if (elm.matches(selector)) break;

    // Otherwise, push it to the siblings array
    siblings.push(elm);

    // Get the next sibling element
    elm = elm.nextElementSibling;
  }
  return siblings;
};

/**
 * --------------------------------------------------
 * Function utilities
 * --------------------------------------------------
 */

export const listenOnce = (element, event, fn) => {
  element.addEventListener(event, function listener(e) {
    element.removeEventListener(event, listener);
    fn(e);
  });
};

export const debounce = (fn, delay) => {
  let timer = null;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      fn(...args);
    }, delay);
  };
};

/**
 * memoize.js
 * by @philogb and @addyosmani
 * with further optimizations by @mathias
 * and @DmitryBaranovsk
 * perf tests: http://bit.ly/q3zpG3
 * Released under an MIT license.
 */
export const memoize = (fn) => {
  return function _memoize(...args) {
    let hash = "";
    let i = args.length;
    let currentArg = null;
    while (i--) {
      currentArg = args[i];
      hash +=
        currentArg === Object(currentArg)
          ? JSON.stringify(currentArg)
          : currentArg;
      if (!fn.memoize) {
        fn.memoize = {};
      }
    }
    if (!(hash in fn.memoize)) {
      fn.memoize[hash] = fn.apply(this, args);
    }
    return fn.memoize[hash];
  };
};

export const not =
  (fn) =>
  (...args) =>
    !fn(...args);

/**
 * --------------------------------------------------
 * String utilities
 * --------------------------------------------------
 */
export const regexFrom = (str) => new RegExp(str, "i");

export const match = _.curry((re, str) => {
  return re.test(str);
});

/* eslint-disable */
export const startsWith = _.curry(
  (searchString, str) => str.substr(0, searchString.length) === searchString,
);
/* eslint-enable */

// Taken from https://gist.github.com/niyazpk/f8ac616f181f6042d1e0
export const updateQueryParameter = (uri, key, value) => {
  // remove the hash part before operating on the uri
  const i = uri.indexOf("#");
  const hash = i === -1 ? "" : uri.substr(i);
  uri = i === -1 ? uri : uri.substr(0, i);
  const re = new RegExp(`([?&])${key}=.*?(&|$)`, "i");
  const separator = uri.indexOf("?") !== -1 ? "&" : "?";
  if (uri.match(re)) {
    uri = uri.replace(re, `$1${key}=${value}$2`);
  } else {
    uri = `${uri}${separator}${key}=${value}`;
  }
  return uri + hash; // finally append the hash as well
};

/**
 * --------------------------------------------------
 * Misc
 * --------------------------------------------------
 */
export const request = memoize(
  (url, method = "get", data = {}, headers = {}) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.onload = function onLoad() {
        if (this.status >= 200 && this.status < 300) {
          resolve(xhr.response);
        } else {
          reject(xhr.statusText);
        }
      };
      xhr.onerror = function onError() {
        reject(xhr.statusText);
      };
      xhr.open(method, url);

      Object.entries(headers).forEach(([key, value]) => {
        xhr.setRequestHeader(key, value);
      });

      xhr.send(data);
    });
  },
);
